// src/components/Sidebar.jsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Typography, Avatar, Divider, Tooltip,
  IconButton, Collapse, alpha,
} from '@mui/material';
import {
  Dashboard, Assignment, BarChart, People, Settings,
  Logout, ChevronLeft, ChevronRight, ExpandLess, ExpandMore,
  DeleteSweep, FileDownload, Restore, LocationOn, ListAlt, ManageAccounts,
} from '@mui/icons-material';
import { logout, selectUser } from '../store/authSlice';

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_MINI = 72;

const NAV = [
  { label: 'Activity', icon: <LocationOn />, path: '/activity' },
  {
    label: 'Tasks', icon: <Assignment />,
    children: [
      { label: 'All Tasks', icon: <ListAlt />, path: '/tasks' },
      { label: 'Task Forms', icon: <FileDownload />, path: '/taskforms' },
    ]
  },
  {
    label: 'Users', icon: <People />,
    children: [
      { label: 'All Users', icon: <ManageAccounts />, path: '/users' },
      { label: 'User Logs', icon: <BarChart />, path: '/userlogs' },
    ]
  },
  // { label: 'Tag Board', icon: <LocationOn />, path: '/tagboard' },
];

// Build default expanded state so ALL groups are open on first load
const DEFAULT_EXPANDED = NAV.reduce((acc, item) => {
  if (item.children) acc[item.label] = true;
  return acc;
}, {});

export default function Sidebar({ open, onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [expandedItems, setExpandedItems] = useState(DEFAULT_EXPANDED);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const toggleExpand = (label) => {
    setExpandedItems((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const fullName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
    : 'Admin';

  const initials = fullName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? DRAWER_WIDTH : DRAWER_WIDTH_MINI,
        flexShrink: 0,
        transition: 'width 0.25s ease',
        '& .MuiDrawer-paper': {
          width: open ? DRAWER_WIDTH : DRAWER_WIDTH_MINI,
          transition: 'width 0.25s ease',
          overflow: 'hidden',
          background: '#fff',
          borderRight: '1px solid rgba(16,117,186,0.12)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center',
        px: open ? 2.5 : 1.5, py: 2.5,
        minHeight: 72,
        borderBottom: '1px solid rgba(16,117,186,0.1)',
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <img src={"logo.png"} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </Box>
        {open && (
          <Box sx={{ ml: 1.5, overflow: 'hidden' }}>
            <Typography variant="h6" sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1075BA', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
              Admin Panel
            </Typography>
            <Typography variant="caption" sx={{ color: '#3a7aaa', fontSize: '0.7rem' }}>
              Management Console
            </Typography>
          </Box>
        )}
        <IconButton
          onClick={onToggle}
          size="small"
          sx={{
            ml: 'auto', flexShrink: 0,
            color: '#6b6b6b',
            '&:hover': { color: '#1075BA', background: 'rgba(16,117,186,0.1)' },
          }}
        >
          {open ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
        </IconButton>
      </Box>

      {/* Nav */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pt: 1 }}>
        <List disablePadding>
          {NAV.map((item) => {
            const active = isActive(item.path);
            const hasChildren = !!item.children;
            const expanded = expandedItems[item.label];

            return (
              <React.Fragment key={item.label}>
                <Tooltip title={!open ? item.label : ''} placement="right">
                  <ListItem disablePadding sx={{ display: 'block', px: 1, mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => hasChildren ? toggleExpand(item.label) : navigate(item.path)}
                      sx={{
                        borderRadius: '10px',
                        minHeight: 44,
                        px: open ? 1.5 : 1,
                        justifyContent: open ? 'initial' : 'center',
                        background: active ? 'rgba(16,117,186,0.15)' : 'transparent',
                        '&:hover': { background: 'rgba(16,117,186,0.1)' },
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': active ? {
                          content: '""',
                          position: 'absolute', left: 0, top: '20%', bottom: '20%',
                          width: 3, borderRadius: '0 3px 3px 0',
                          background: '#1075BA',
                        } : {},
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: 0,
                        mr: open ? 1.5 : 0,
                        justifyContent: 'center',
                        color: active ? '#1075BA' : '#6b6b6b',
                        '& .MuiSvgIcon-root': { fontSize: 20 },
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      {open && (
                        <>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                              fontWeight: active ? 600 : 400,
                              color: active ? '#1075BA' : '#000',
                            }}
                          />
                          {hasChildren && (expanded ? <ExpandLess sx={{ color: '#6b6b6b', fontSize: 18 }} /> : <ExpandMore sx={{ color: '#6b6b6b', fontSize: 18 }} />)}
                        </>
                      )}
                    </ListItemButton>
                  </ListItem>
                </Tooltip>

                {/* Children */}
                {hasChildren && open && (
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <List disablePadding sx={{ pl: 2 }}>
                      {item.children.map((child) => {
                        const childActive = location.pathname === child.path;
                        return (
                          <ListItem disablePadding key={child.label} sx={{ px: 1, mb: 0.25 }}>
                            <ListItemButton
                              onClick={() => navigate(child.path)}
                              sx={{
                                borderRadius: '8px', minHeight: 36, px: 1.5,
                                background: childActive ? 'rgba(16,117,186,0.12)' : 'transparent',
                                '&:hover': { background: 'rgba(16,117,186,0.08)' },
                              }}
                            >
                              <ListItemIcon sx={{
                                minWidth: 0, mr: 1.5,
                                color: childActive ? '#1075BA' : '#6b6b6b',
                                '& .MuiSvgIcon-root': { fontSize: 16 },
                              }}>
                                {child.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={child.label}
                                primaryTypographyProps={{
                                  fontSize: '0.8rem',
                                  fontWeight: childActive ? 600 : 400,
                                  color: childActive ? '#1075BA' : '#000',
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      {/* User section */}
      <Box sx={{ borderTop: '1px solid rgba(16,117,186,0.1)', p: 1.5 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center',
          px: open ? 1 : 0, py: 1,
          borderRadius: '10px',
          justifyContent: open ? 'flex-start' : 'center',
        }}>
          <Avatar sx={{
            width: 36, height: 36, flexShrink: 0,
            background: 'linear-gradient(135deg, #1075BA, #1a8fd6)',
            fontSize: '0.8rem', fontWeight: 700,
          }}>
            {initials}
          </Avatar>
          {open && (
            <Box sx={{ ml: 1.5, flex: 1, overflow: 'hidden' }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1075BA', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {fullName}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#3a7aaa' }}>
                Administrator
              </Typography>
            </Box>
          )}
          {open && (
            <Tooltip title="Sign out">
              <IconButton size="small" onClick={handleLogout} sx={{ color: '#6b6b6b', '&:hover': { color: '#f44336', background: 'rgba(244,67,54,0.1)' } }}>
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {!open && (
          <Tooltip title="Sign out" placement="right">
            <IconButton size="small" onClick={handleLogout} sx={{ mt: 0.5, color: '#6b6b6b', display: 'flex', mx: 'auto', '&:hover': { color: '#f44336' } }}>
              <Logout fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Drawer>
  );
}